import React, { useEffect, useState } from 'react'
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Typography,
	Checkbox,
	CircularProgress,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getSmallDirectReports } from '../../../../../services/laborService'
import { SmallWorker } from '../../../../../types/Employee/Employee'
import { useParams } from 'react-router-dom'
import { useTokenClaims } from '../../../../../security/useTokenClaims'
import { createTimesheet } from '../../../../../services/shiftEndReportService'
import { useShiftEndContext } from '../../ShiftEndContext'

const CreateTimesheetButton = () => {
	const [open, setOpen] = useState(false)
	const params = useParams()
	const token = useTokenClaims()
	const foremanId = token?.role.title === 'Foreman' ? Number(token.id) : Number(params.id)
	const [laborers, setLaborers] = useState<SmallWorker[]>([])
	const [copyFromLaborers, setCopyFromLaborers] = useState<SmallWorker[]>([])
	const [selectedCopyFromLaborerId, setSelectedCopyFromLaborerId] = useState<number>()
	const [loading, setLoading] = useState(true)
	const [creating, setCreating] = useState(false)
	const [selectedWorkerIds, setSelectedWorkerIds] = useState<number[]>([])
	const { shiftDate, workerSummary, loadData } = useShiftEndContext()

	const handleClickOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
		setSelectedWorkerIds([])
		setSelectedCopyFromLaborerId(undefined)
		setCopyFromLaborers([])
	}

	useEffect(() => {
		if (open) {
			setLoading(true)
			;(async () => {
				try {
					const response = await getSmallDirectReports(foremanId)
					setLaborers(
						response.filter(
							(worker) => !workerSummary.some((timesheet) => timesheet.workerId === worker.id),
						),
					)
					setCopyFromLaborers(
						response.filter((worker) =>
							workerSummary.some(
								(timesheet) => timesheet.workerId === worker.id && timesheet.activities.length > 0,
							),
						),
					)
				} finally {
					setLoading(false)
				}
			})()
		}
	}, [open, foremanId])

	const handleWorkerToggle = (workerId: number) => {
		setSelectedWorkerIds((prev) => {
			if (prev.includes(workerId)) {
				return prev.filter((id) => id !== workerId)
			} else {
				return [...prev, workerId]
			}
		})
	}

	const handleSelectAll = () => {
		if (selectedWorkerIds.length === laborers.length) {
			setSelectedWorkerIds([])
		} else {
			setSelectedWorkerIds(laborers.map((laborer) => laborer.id))
		}
	}

	const handleCreateTimesheet = async () => {
		setCreating(true)
		try {
			await createTimesheet(selectedWorkerIds, shiftDate, foremanId, selectedCopyFromLaborerId)
			await loadData(foremanId)
		} finally {
			setCreating(false)
		}
		handleClose()
	}

	const allSelected = laborers.length > 0 && selectedWorkerIds.length === laborers.length
	const someSelected = selectedWorkerIds.length > 0 && selectedWorkerIds.length < laborers.length

	return (
		<>
			<Button
				variant='outlined'
				color='primary'
				onClick={handleClickOpen}
				sx={{ color: 'primary.main' }}
			>
				<AddIcon />
				Create New Timesheet
			</Button>

			<Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
				<DialogTitle>
					Create New Timesheet
					<IconButton
						onClick={handleClose}
						sx={(theme) => ({
							position: 'absolute',
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Typography variant='body1' sx={{ mb: 2 }}>
						Select workers to create timesheets for:
					</Typography>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : laborers.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<Typography variant='body1'>
								All laborers already have a timesheet for this date.
							</Typography>
						</Box>
					) : (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									cursor: 'pointer',
									pb: 1,
									borderBottom: '1px solid',
									borderColor: 'divider',
									mb: 1,
									'&:hover': {
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
									},
								}}
								onClick={handleSelectAll}
							>
								<Checkbox checked={allSelected} indeterminate={someSelected} />
								<Typography variant='body1' fontWeight={500}>
									Select All
								</Typography>
							</Box>
							{laborers.map((laborer) => (
								<Box
									key={laborer.id}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										cursor: 'pointer',
										'&:hover': {
											backgroundColor: 'rgba(0, 0, 0, 0.04)',
										},
									}}
									onClick={() => handleWorkerToggle(laborer.id)}
								>
									<Checkbox checked={selectedWorkerIds.includes(laborer.id)} />
									<Typography variant='body1'>{laborer.fullName}</Typography>
								</Box>
							))}

							{copyFromLaborers.length > 0 && (
								<Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
									<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
										<ContentCopyIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
										Copy timesheet from (optional)
									</Typography>
									<FormControl fullWidth size='small'>
										<InputLabel id='copy-from-label'>Select worker to copy from</InputLabel>
										<Select
											labelId='copy-from-label'
											value={selectedCopyFromLaborerId ?? ''}
											label='Select worker to copy from'
											onChange={(e) =>
												setSelectedCopyFromLaborerId(
													e.target.value === '' ? undefined : (e.target.value as number),
												)
											}
										>
											<MenuItem value=''>
												<em>None – create empty timesheets</em>
											</MenuItem>
											{copyFromLaborers.map((worker) => (
												<MenuItem key={worker.id} value={worker.id}>
													{worker.fullName}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									{selectedCopyFromLaborerId != null && (
										<Typography
											variant='caption'
											color='text.secondary'
											sx={{ mt: 1, display: 'block' }}
										>
											Tasks and time records from the selected worker will be copied to each new
											timesheet.
										</Typography>
									)}
								</Box>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button color='warning' variant='contained' onClick={handleClose}>
						Cancel
					</Button>
					<Button
						color='success'
						variant='contained'
						onClick={handleCreateTimesheet}
						disabled={creating || selectedWorkerIds.length === 0}
					>
						{creating ? <CircularProgress size={20} sx={{ m: 0, p: 0 }} /> : 'Create'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default CreateTimesheetButton
